/**
 * NameRulesPanel - Pravila za transformaciju naziva proizvoda po dobavljaču
 * Omogućava konfiguraciju kako generirati naziv za svakog dobavljača
 */

import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  Alert,
  Grid,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  TextFields as TextIcon,
  Description as DescIcon,
} from '@mui/icons-material';
import {
  NameRulesConfig,
  NameRule,
  NameTransformation,
  NameSourceType,
  DEFAULT_NAME_RULES_CONFIG,
  DEFAULT_NAME_TRANSFORMATION,
} from '../../types/shop';

interface NameRulesPanelProps {
  config: NameRulesConfig;
  onChange: (config: NameRulesConfig) => void;
  availableSuppliers?: string[];
}

// Opcije za izvor naziva
const NAME_SOURCE_OPTIONS: { value: NameSourceType; label: string; description: string }[] = [
  { value: 'name', label: 'Naziv (name)', description: 'Koristi originalni naziv proizvoda' },
  { value: 'description', label: 'Opis (description)', description: 'Generiraj naziv iz opisa proizvoda' },
  { value: 'short_description', label: 'Kratki opis', description: 'Generiraj naziv iz kratkog opisa' },
  { value: 'sku', label: 'SKU', description: 'Koristi SKU kao naziv' },
  { value: 'custom_field', label: 'Custom polje', description: 'Koristi vrijednost iz custom polja' },
];

// Opcije za text case
const TEXT_CASE_OPTIONS = [
  { value: 'none', label: 'Bez promjene' },
  { value: 'uppercase', label: 'VELIKO SLOVO' },
  { value: 'lowercase', label: 'malo slovo' },
  { value: 'titlecase', label: 'Naslov Case' },
];

// Znakovi koji se mogu ukloniti
const COMMON_REMOVE_CHARS = ['(', ')', ',', '[', ']', '{', '}', '"', "'", '/', '\\', '|', '*'];

export default function NameRulesPanel({ config, onChange, availableSuppliers = [] }: NameRulesPanelProps) {
  // Dodaj novo pravilo
  const addRule = () => {
    const newRule: NameRule = {
      supplierName: '',
      enabled: true,
      source: 'description',
      transformation: {
        maxWords: 15,
        removeChars: ['(', ')', ','],
        stripHtml: true,
        trim: true,
        textCase: 'none',
      },
      priority: config.rules.length + 1,
    };

    onChange({
      ...config,
      rules: [...config.rules, newRule],
    });
  };

  // Ukloni pravilo
  const removeRule = (index: number) => {
    const newRules = config.rules.filter((_, i) => i !== index);
    onChange({
      ...config,
      rules: newRules,
    });
  };

  // Ažuriraj pravilo
  const updateRule = (index: number, updates: Partial<NameRule>) => {
    const newRules = [...config.rules];
    newRules[index] = { ...newRules[index], ...updates };
    onChange({
      ...config,
      rules: newRules,
    });
  };

  // Ažuriraj transformaciju pravila
  const updateRuleTransformation = (index: number, updates: Partial<NameTransformation>) => {
    const newRules = [...config.rules];
    newRules[index] = {
      ...newRules[index],
      transformation: { ...newRules[index].transformation, ...updates },
    };
    onChange({
      ...config,
      rules: newRules,
    });
  };

  // Pomjeri pravilo gore/dolje
  const moveRule = (index: number, direction: 'up' | 'down') => {
    const newRules = [...config.rules];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newRules.length) return;

    [newRules[index], newRules[newIndex]] = [newRules[newIndex], newRules[index]];
    // Ažuriraj prioritete
    newRules.forEach((rule, i) => {
      rule.priority = i + 1;
    });

    onChange({
      ...config,
      rules: newRules,
    });
  };

  return (
    <Box>
      {/* Header sa switch-om */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextIcon color="primary" />
          <Typography variant="h6">Pravila naziva po dobavljaču</Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={config.enabled}
              onChange={(e) => onChange({ ...config, enabled: e.target.checked })}
              color="primary"
            />
          }
          label={config.enabled ? 'Uključeno' : 'Isključeno'}
        />
      </Box>

      {/* Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Definirajte kako generirati naziv proizvoda za svakog dobavljača.
        Na primjer, za ASBIS možete uzeti prvih 15 riječi iz opisa i ukloniti zagrade.
      </Alert>

      {config.enabled && (
        <>
          {/* Lista pravila */}
          {config.rules.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nema definiranih pravila. Kliknite "Dodaj pravilo" da kreirate novo.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {config.rules.map((rule, index) => (
                <Card key={index} variant="outlined" sx={{
                  borderColor: rule.enabled ? 'primary.main' : 'divider',
                  opacity: rule.enabled ? 1 : 0.7,
                }}>
                  <CardContent>
                    {/* Header pravila */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <DragIcon sx={{ color: 'text.disabled', cursor: 'grab' }} />
                      <Chip
                        size="small"
                        label={`#${index + 1}`}
                        color={rule.enabled ? 'primary' : 'default'}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Autocomplete
                          freeSolo
                          size="small"
                          value={rule.supplierName}
                          onChange={(_, value) => updateRule(index, { supplierName: value || '' })}
                          onInputChange={(_, value) => updateRule(index, { supplierName: value })}
                          options={availableSuppliers}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Dobavljač"
                              placeholder="Npr. ASBIS, COMTRADE..."
                              size="small"
                            />
                          )}
                          sx={{ minWidth: 200 }}
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={rule.enabled}
                            onChange={(e) => updateRule(index, { enabled: e.target.checked })}
                            size="small"
                          />
                        }
                        label=""
                      />
                      <IconButton
                        size="small"
                        onClick={() => moveRule(index, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveRule(index, 'down')}
                        disabled={index === config.rules.length - 1}
                      >
                        <ArrowDownIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRule(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Konfiguracija pravila */}
                    <Grid container spacing={2}>
                      {/* Izvor naziva */}
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Izvor naziva</InputLabel>
                          <Select
                            value={rule.source}
                            label="Izvor naziva"
                            onChange={(e) => updateRule(index, { source: e.target.value as NameSourceType })}
                          >
                            {NAME_SOURCE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                <Box>
                                  <Typography variant="body2">{opt.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {opt.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Custom field key (ako je source = custom_field) */}
                      {rule.source === 'custom_field' && (
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Custom polje"
                            value={rule.customFieldKey || ''}
                            onChange={(e) => updateRule(index, { customFieldKey: e.target.value })}
                            placeholder="Npr. _naziv_proizvoda"
                          />
                        </Grid>
                      )}

                      {/* Max riječi */}
                      <Grid item xs={6} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Max riječi"
                          value={rule.transformation.maxWords || ''}
                          onChange={(e) => updateRuleTransformation(index, {
                            maxWords: e.target.value ? parseInt(e.target.value) : undefined
                          })}
                          placeholder="15"
                          InputProps={{ inputProps: { min: 1, max: 100 } }}
                        />
                      </Grid>

                      {/* Text case */}
                      <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Velika/mala slova</InputLabel>
                          <Select
                            value={rule.transformation.textCase || 'none'}
                            label="Velika/mala slova"
                            onChange={(e) => updateRuleTransformation(index, {
                              textCase: e.target.value as any
                            })}
                          >
                            {TEXT_CASE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Znakovi za uklanjanje */}
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          freeSolo
                          size="small"
                          value={rule.transformation.removeChars || []}
                          onChange={(_, value) => updateRuleTransformation(index, { removeChars: value })}
                          options={COMMON_REMOVE_CHARS}
                          renderTags={(value, getTagProps) =>
                            value.map((option, i) => (
                              <Chip
                                variant="outlined"
                                label={option === ' ' ? '(space)' : option}
                                size="small"
                                {...getTagProps({ index: i })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Ukloni znakove"
                              placeholder="Dodaj znak..."
                            />
                          )}
                        />
                      </Grid>

                      {/* Prefix */}
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Prefix"
                          value={rule.transformation.prefix || ''}
                          onChange={(e) => updateRuleTransformation(index, { prefix: e.target.value })}
                          placeholder="Npr. [ASBIS] "
                        />
                      </Grid>

                      {/* Suffix */}
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Suffix"
                          value={rule.transformation.suffix || ''}
                          onChange={(e) => updateRuleTransformation(index, { suffix: e.target.value })}
                          placeholder="Npr. - Novo"
                        />
                      </Grid>
                    </Grid>

                    {/* Primjer transformacije */}
                    {rule.supplierName && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Primjer za {rule.supplierName}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                          {rule.source === 'description'
                            ? `Uzmi ${rule.transformation.maxWords || 'sve'} riječi iz opisa`
                            : rule.source === 'name'
                            ? 'Koristi originalni naziv'
                            : `Koristi ${rule.source}`}
                          {rule.transformation.removeChars?.length
                            ? ` → ukloni: ${rule.transformation.removeChars.join(' ')}`
                            : ''}
                          {rule.transformation.prefix ? ` → prefix: "${rule.transformation.prefix}"` : ''}
                          {rule.transformation.suffix ? ` → suffix: "${rule.transformation.suffix}"` : ''}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Dugme za dodavanje */}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addRule}
            sx={{ mb: 3 }}
          >
            Dodaj pravilo
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* Default pravilo */}
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescIcon fontSize="small" />
            Default pravilo (za ostale dobavljače)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Primjenjuje se na dobavljače koji nemaju specifično pravilo
          </Typography>

          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Izvor naziva</InputLabel>
                    <Select
                      value={config.defaultRule?.source || 'name'}
                      label="Izvor naziva"
                      onChange={(e) => onChange({
                        ...config,
                        defaultRule: {
                          ...config.defaultRule,
                          source: e.target.value as NameSourceType,
                          transformation: config.defaultRule?.transformation || DEFAULT_NAME_TRANSFORMATION,
                        },
                      })}
                    >
                      {NAME_SOURCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
